import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Bell, BellOff } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface EventNotificationSettingsProps {
  eventId: number;
  eventName: string;
}

export function EventNotificationSettings({ eventId, eventName }: EventNotificationSettingsProps) {
  const { toast } = useToast();
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [eventEnabled, setEventEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch notification preference data
  const fetchPreference = async () => {
    try {
      setIsLoading(true);
      const response = await apiRequest("GET", `/api/events/${eventId}/notification-preference`);
      const data = await response.json();
      setGlobalEnabled(data.globalEnabled && data.hasSubscription);
      setEventEnabled(data.eventEnabled);
    } catch (error) {
      console.error("Failed to fetch notification preference:", error);
      setGlobalEnabled(false);
      setEventEnabled(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Update notification preference
  const updatePreference = async (enabled: boolean) => {
    try {
      setIsUpdating(true);
      const response = await apiRequest("PUT", `/api/events/${eventId}/notification-preference`, { enabled });
      const data = await response.json();
      
      // Update local state immediately
      setEventEnabled(data.enabled);
      
      toast({
        title: data.enabled ? "Notifications Enabled" : "Notifications Disabled",
        description: `You will ${data.enabled ? "now" : "no longer"} receive notifications for ${eventName}`,
      });
    } catch (error) {
      console.error("Failed to update notification preference:", error);
      toast({
        title: "Error",
        description: "Failed to update notification preference",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Fetch data on mount and when eventId changes
  useEffect(() => {
    fetchPreference();
  }, [eventId]);

  if (isLoading) {
    return null;
  }

  // User hasn't enabled push notifications globally
  if (!globalEnabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="h-5 w-5" />
            Event Notifications
          </CardTitle>
          <CardDescription>
            Enable push notifications globally to receive notifications for this event.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Bell className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-800">Enable Global Notifications First</h4>
                <p className="text-sm text-blue-700 mt-1">
                  To receive notifications for this event, you need to enable push notifications in your settings.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Event Notifications
        </CardTitle>
        <CardDescription>
          Opt in to receive notifications for this specific event.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Enable/Disable Event Notifications */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="text-base">Opt in to Notifications</Label>
            <div className="text-sm text-muted-foreground">
              Choose to receive stage calls, queue updates, and chat messages for {eventName}.
            </div>
          </div>
          <Switch
            checked={eventEnabled}
            onCheckedChange={updatePreference}
            disabled={isUpdating}
          />
        </div>

        {/* Notification Status */}
        <div className="flex items-center justify-between">
          <Label>Status</Label>
          {eventEnabled ? (
            <Badge variant="default" className="gap-1">
              <Bell className="h-3 w-3" />
              Opted In
            </Badge>
          ) : (
            <Badge variant="secondary" className="gap-1">
              <BellOff className="h-3 w-3" />
              Not Opted In
            </Badge>
          )}
        </div>

        {/* Notification Types Info */}
        {eventEnabled && (
          <div className="space-y-3">
            <Label className="text-base">You're receiving notifications for:</Label>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <span>• Stage calls when you're selected to perform</span>
              </div>
              <div className="flex items-center gap-2">
                <span>• Queue updates when the performance order changes</span>
              </div>
              <div className="flex items-center gap-2">
                <span>• Chat messages from other participants</span>
              </div>
              <div className="flex items-center gap-2">
                <span>• Important event announcements</span>
              </div>
            </div>
          </div>
        )}
        
        {!eventEnabled && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Bell className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-800">Not Receiving Notifications</h4>
                <p className="text-sm text-blue-700 mt-1">
                  You won't receive any notifications for this event. Toggle the switch above to opt in.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}